import React from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { WelcomeReward } from '@gamiphy/loyalty-station-desktop/components/welcomeGift/reward';
import { Trans } from '@gamiphy/react-i18n';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { observer } from 'mobx-react';
import { getDiffInDays } from '@gamiphy/utils/date';
import { useOnCopy } from '@gamiphy/utils/react';
import SquaredCopy from '@gamiphy/icons/SquaredCopy';
import CheckIcon from '@mui/icons-material/Check';
import { DiscountsStore } from '@gamiphy/loyalty-station-desktop/store/discounts';

const WelcomeCouponCard: React.FC = observer(() => {
  const {
    copied,
    onCopy,
  } = useOnCopy(DiscountsStore.welcomeGiftDiscount?.code);
  const theme = useTheme();
  const loading = !DiscountsStore.welcomeGiftDiscount;

  React.useEffect(() => {
    void DiscountsStore.generateWelcomeGiftDiscount();
  }, []);

  return (
    <Card sx={{ textAlign: loading ? 'center' : 'left' }}>
      <CardContent>
        {
          DiscountsStore.welcomeGiftDiscount ? (
            <Grid container direction="column" spacing={1}>
              <Grid>
                <Box sx={{
                  borderRadius: '0.4rem',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.secondary,
                  padding: theme.spacing(1, 2),
                }}
                >
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid>
                      <Trans
                        i18nKey="welcomeGift.discount.welcomeCoupon"
                        defaults="Welcome coupon"
                      />
                    </Grid>
                    <Grid>
                      <WelcomeReward sx={{
                        width: '7rem',
                        fontSize: '1.2rem',
                      }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid>
                <Trans
                  i18nKey="welcomeGift.discount.expiryMessage"
                  defaults="You can now use your welcome coupon for the next {{remainingDays}} days!"
                  values={{
                    remainingDays: DiscountsStore.welcomeGiftDiscount.expiresAt && getDiffInDays(new Date(DiscountsStore.welcomeGiftDiscount.expiresAt), new Date()),
                  }}
                />
              </Grid>
              <Grid>
                <Box sx={{
                  borderRadius: '0.6rem',
                  border: 'solid 1px #e5e5e5',
                  background: '#fafafa',
                  padding: theme.spacing(2),
                  textAlign: 'center',
                }}
                >
                  <Trans
                    i18nKey="welcomeGift.discount.code"
                    defaults="Coupon code: {{code}}"
                    values={{
                      code: DiscountsStore.welcomeGiftDiscount.code,
                    }}
                  />
                </Box>
              </Grid>
              <Grid>
                <Button
                  color="primary"
                  variant="contained"
                  endIcon={copied
                    ? (
                      <CheckIcon
                        sx={{
                          width: '1.8rem',
                          height: '1.8rem',
                          color: theme.palette.text.secondary,
                        }}
                        fontSize="large"
                      />
                    )
                    : (
                      <SquaredCopy
                        color={ThemeStore.theme.palette?.text?.secondary}
                        style={{
                          width: '1.8rem',
                          height: '1.8rem',
                          color: theme.palette.text.secondary,
                        }}
                      />
                    )}
                  onClick={() => void onCopy()}
                  fullWidth
                >
                  {
                    copied ? (
                      <Trans
                        i18nKey="copied"
                        defaults="Copied"
                      />
                    ) : (
                      <Trans
                        i18nKey="shared.copyCode"
                        defaults="Copy Code"
                      />
                    )
                  }
                </Button>
              </Grid>
            </Grid>
          ) : (
            <CircularProgress color="primary" />
          )
        }
      </CardContent>
    </Card>
  );
});

export default WelcomeCouponCard;
